import { t } from "@lingui/macro";
import {
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { formatExperienceDates } from "@reactive-resume/utils";
import { useMemo } from "react";

/** The dates as the form holds them while being picked: a month or year can still be empty. */
export type DatesDraft = {
  start: { year: number | null; month: number | null } | null;
  end: { year: number | null; month: number | null } | null;
  current: boolean;
  source: "entered" | "parsed" | "backfill";
};

type Props = {
  value: DatesDraft | undefined;
  /** The text the resume prints today (kept until dates are picked). */
  text: string;
  onChange: (dates: DatesDraft | undefined, text: string) => void;
  error?: string;
};

const NONE = "none";
// The editor panel sits at z-[55] (section-dialog.tsx); the lists must open above it, or they open
// out of sight while still capturing the mouse.
const LIST_CLASS = "z-[60] max-h-72";

/** Complete dates as the resume prints them, or null while a month or year is still missing. */
export function draftText(d: DatesDraft | undefined): string | null {
  if (!d?.start?.year || !d.start.month) return null;
  if (!d.current && (!d.end?.year || !d.end.month)) return null;
  return formatExperienceDates({
    start: { year: d.start.year, month: d.start.month },
    end: d.current ? null : { year: d.end!.year!, month: d.end!.month },
    current: d.current,
  });
}

/**
 * Job dates entered like LinkedIn: "I currently work here", then a start month and year and, unless
 * current, an end month and year. No day. The text every template prints ("Mar 2019 - Present") is
 * written from the picked dates.
 */
export const ExperienceDatesField = ({ value, text, onChange, error }: Props) => {
  const months = useMemo(() => {
    const format = new Intl.DateTimeFormat(undefined, { month: "long" });
    return Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1),
      label: format.format(new Date(2000, i, 1)),
    }));
  }, []);
  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: now - 1950 + 1 }, (_, i) => String(now - i));
  }, []);

  const draft: DatesDraft = value ?? { start: null, end: null, current: false, source: "entered" };
  const shown = draftText(draft);

  // Any change is the person's own entry, and the printed text follows the dates once complete.
  const update = (patch: Partial<DatesDraft>) => {
    const next: DatesDraft = { ...draft, ...patch, source: "entered" };
    const empty =
      !next.start?.year &&
      !next.start?.month &&
      !next.end?.year &&
      !next.end?.month &&
      !next.current;
    onChange(empty ? undefined : next, draftText(next) ?? text);
  };
  const setPart = (which: "start" | "end", part: "year" | "month", v: string) => {
    const current = draft[which] ?? { year: null, month: null };
    update({
      [which]: { ...current, [part]: v === NONE ? null : Number(v) },
    } as Partial<DatesDraft>);
  };

  const picker = (which: "start" | "end", label: string) => {
    const d = draft[which];
    return (
      <fieldset className="space-y-1.5">
        <legend className="mb-1.5 text-sm font-medium leading-none">{label}</legend>
        <div className="grid grid-cols-[1fr_7rem] gap-2">
          <Select
            value={d?.month ? String(d.month) : NONE}
            onValueChange={(v) => setPart(which, "month", v)}
          >
            <SelectTrigger aria-label={t`${label} month`}>
              <SelectValue placeholder={t`Month`} />
            </SelectTrigger>
            <SelectContent className={LIST_CLASS}>
              <SelectItem value={NONE}>{t`Month`}</SelectItem>
              {months.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={d?.year ? String(d.year) : NONE}
            onValueChange={(v) => setPart(which, "year", v)}
          >
            <SelectTrigger aria-label={t`${label} year`}>
              <SelectValue placeholder={t`Year`} />
            </SelectTrigger>
            <SelectContent className={LIST_CLASS}>
              <SelectItem value={NONE}>{t`Year`}</SelectItem>
              {years.map((y) => (
                <SelectItem key={y} value={y}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </fieldset>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Checkbox
          id="experience-current"
          checked={draft.current}
          onCheckedChange={(checked) =>
            update({ current: checked === true, end: checked === true ? null : draft.end })
          }
        />
        <Label htmlFor="experience-current" className="cursor-pointer text-sm font-normal">
          {t`I currently work here`}
        </Label>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {picker("start", t`Start date`)}
        {!draft.current && picker("end", t`End date`)}
      </div>

      {error ? (
        <p className="text-sm font-medium text-error" role="alert">
          {error}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {shown
            ? t`Shown on your resume as “${shown}”`
            : text
              ? t`Your resume shows “${text}”. Pick a month and year to update it.`
              : t`Leave the dates empty if you'd rather not show them.`}
        </p>
      )}
      {(value || text) && (
        <button
          type="button"
          onClick={() => onChange(undefined, "")}
          className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          {t`Clear dates`}
        </button>
      )}
    </div>
  );
};
